/**
 * Componente copiado de ADF 5.0.0 y adaptado a nuestras necesidades
 */

import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
    MOMENT_DATE_FORMATS,
    MomentDateAdapter,
    UserPreferencesService,
    UserPreferenceValues
} from '@alfresco/adf-core';

import { Moment } from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SearchQueryBuilderService, SearchWidget } from '@alfresco/adf-content-services';
import { LiveErrorStateMatcher } from './live-error-state-matcher';
import { CustomSearchWidgetSettings } from 'projects/alden-extension/src/lib/models/custom-viewer-config';

export interface DateRangeValue {
    from: string;
    to: string;
}

declare let moment: any;

const DEFAULT_FORMAT_DATE: string = 'DD/MM/YYYY';

@Component({
    selector: 'smt-search-date-range',
    templateUrl: './search-date-range.component.html',
    styleUrls: ['./search-date-range.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MOMENT_DATE_FORMATS }
    ],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'smt-search-date-range' }
})
export class SMTSearchDateRangeComponent implements SearchWidget, OnInit, OnDestroy {

    from: UntypedFormControl;
    to: UntypedFormControl;

    form: UntypedFormGroup;
    matcher = new LiveErrorStateMatcher();

    id: string;
    settings?: CustomSearchWidgetSettings;
    context?: SearchQueryBuilderService;
    datePickerFormat: string;
    maxDate: any;
    fromMaxDate: any;
    startValue: any;
    displayValue$: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);

    //Custom SMT
    query: string;
    editable: boolean;
    nullable: boolean;

    _filterIsNull: boolean;
    filterNullApplied: boolean = false;
    get filterIsNull(){
        return this._filterIsNull
    }
    set filterIsNull(valor: boolean) {
        this._filterIsNull = valor;

        if (valor) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    private onDestroy$ = new Subject<boolean>();

    constructor(private dateAdapter: DateAdapter<Moment>, private userPreferencesService: UserPreferencesService) {
        this.editable = true;
        this.nullable = true;
    }

    ngOnInit() {
        this.datePickerFormat = this.settings?.dateFormat ? this.settings.dateFormat : DEFAULT_FORMAT_DATE;

        const customDateAdapter = this.dateAdapter as MomentDateAdapter;
        customDateAdapter.overrideDisplayFormat = this.datePickerFormat;

        this.userPreferencesService
            .select(UserPreferenceValues.Locale)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(locale => this.setLocale(locale));

        const validators = Validators.compose([
            Validators.required
        ]);

        if (this.settings && this.settings.maxDate) {
            if (this.settings.maxDate === 'today') {
                this.maxDate = this.dateAdapter.today().endOf('day');
            } else {
                this.maxDate = moment(this.settings.maxDate).endOf('day');
            }
        }

        if (this.startValue) {
            const splitValue = this.startValue.split('||');
            const fromValue = this.dateAdapter.parse(splitValue[0], this.datePickerFormat);
            const toValue = this.dateAdapter.parse(splitValue[1], this.datePickerFormat);
            this.from = new UntypedFormControl({value:fromValue, disabled: false}, validators);
            this.to = new UntypedFormControl({value:toValue, disabled: false}, validators);
        } else {
            this.from = new UntypedFormControl({value:'', disabled: false}, validators);
            this.to = new UntypedFormControl({value:'', disabled: false}, validators);
        }

        this.form = new UntypedFormGroup({
            from: this.from,
            to: this.to
        });

        this.setFromMaxDate();

        if(this.settings && this.settings.allowUpdateOnChange !== undefined && this.settings.allowUpdateOnChange !== null) {
            this.editable = this.settings.allowUpdateOnChange;
        }
        
        this.checkIfNullable();
    }
        
    checkIfNullable(){
        if(this.settings?.nullable !== undefined && this.settings?.nullable !== null) {
            this.nullable =  this.settings.nullable;
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    getFromValidationMessage(): string {
        return this.from.hasError('invalidOnChange') || this.hasParseError(this.from) ? 'SEARCH.FILTER.VALIDATION.INVALID-DATE' :
            this.from.hasError('matDatepickerMax') ? 'SEARCH.FILTER.VALIDATION.BEYOND-MAX-DATE' :
            this.from.hasError('required') ? 'SEARCH.FILTER.VALIDATION.REQUIRED-VALUE' :
            '';
    }

    getToValidationMessage(): string {
        return this.to.hasError('invalidOnChange') || this.hasParseError(this.to) ? 'SEARCH.FILTER.VALIDATION.INVALID-DATE' :
            this.to.hasError('matDatepickerMin') ? 'SEARCH.FILTER.VALIDATION.NO-DAYS' :
            this.to.hasError('matDatepickerMax') ? 'SEARCH.FILTER.VALIDATION.BEYOND-MAX-DATE' :
            this.to.hasError('required') ? 'SEARCH.FILTER.VALIDATION.REQUIRED-VALUE' :
            '';
    }

    getCurrentValue(): DateRangeValue {
        //Custom SMT
        return {
            from: this.form.value.from ? this.dateAdapter.format(this.form.value.from, this.datePickerFormat): '',
            to: this.form.value.to ? this.dateAdapter.format(this.form.value.to, this.datePickerFormat): ''
        };
    }

    getAppliedValue(): any {
        return this.to? this.dateAdapter.format(this.form.value.from, this.datePickerFormat) + "||" + this.dateAdapter.format(this.form.value.to, this.datePickerFormat) : this.dateAdapter.format(this.form.value.from, this.datePickerFormat)+"";
    }

    hasValidValue(): boolean {
        return this.from.valid || this.to.valid || this.filterIsNull || this.filterNullApplied;
    }

    submitValues() {
        this.updateQuery();
        this.updateDisplayValue();
    }

    reset() {
        this.form.reset({
            from: '',
            to: ''
        });

        if (this.id && this.context) {
            this.context.queryFragments[this.id] = '';
        }
        this.setFromMaxDate();
        this.updateQuery();
        this.updateDisplayValue();
    }

    updateDisplayValue(): void {
        if (!this.hasValidValue()) {
            this.displayValue$.next(null);
        } else if(this.filterIsNull){
            this.displayValue$.next({
                value: 'Sin valor',
                tooltip: 'Sin valor'
            });
        } else {
            const maxLength = 25;
            let value: string;
            if(this.form.valid) { 
                value = `${this.dateAdapter.format(this.form.value.from, this.datePickerFormat)} - ${this.dateAdapter.format(this.form.value.to, this.datePickerFormat)}`;
            } else if(this.from.valid) {
                value = `Desde el ${this.dateAdapter.format(this.form.value.from, this.datePickerFormat)}`;
            } else if(this.to.valid) {
                value = `Hasta el ${this.dateAdapter.format(this.form.value.to, this.datePickerFormat)}`;
            }
            this.displayValue$.next({
                    value: value.substring(0, maxLength) + (value.length > maxLength ? '...' : ''),
                    tooltip: value
            });
        }
    }

    updateQuery() {
        if (this.id && this.context) {
            this.filterNullApplied = this.filterIsNull;
            this.query = '';
            if(this.filterIsNull){
                this.query += `(ISNULL:'${this.settings.field}' OR ISUNSET:'${this.settings.field}' OR NOT @${this.settings.field}:[* TO *])`;
            }else{
                this.formatDateQuery();
                this.context.queryFragments[this.id] = this.query;           
                this.context.update();
            }
        }
    }

    //Custom SMT
    formatDateQuery() {
        const model = this.form.value as { from: string; to: string };
        if(model != null && (this.from.valid || this.to.valid)){
            // TODO: cuando buscas una fecha con hora, salen tambien los del dia anterior
            const start = this.from.valid ? moment(model.from).startOf('day').utc().format() : '*';
            const end = this.to.valid ?  moment(model.to).endOf('day').utc().format() : '*';
            this.query = `+@${this.settings.field}:['${start}' TO '${end}']`;
        }
    }

    //Custom SMT
    getCurrentQuery() {
        return this.query;
    }

    setValue(parsedDate: string) {
        if(parsedDate === undefined){
            this.reset();
        }else{
            const splitValue = parsedDate.split('||');
            const fromValue = this.dateAdapter.parse(splitValue[0], this.datePickerFormat);
            const toValue = this.dateAdapter.parse(splitValue[1], this.datePickerFormat);
            this.from.setValue(fromValue);
            //this.from.markAsDirty();
            //this.from.markAsTouched();
            this.to.setValue(toValue);
            //this.to.markAsDirty();
            //this.to.markAsTouched();
        }
    }

    onChangedHandler(event: any, formControl: UntypedFormControl) {
        const inputValue = event.value;
        const formatDate = this.dateAdapter.parse(inputValue, this.datePickerFormat);
        if (formatDate && formatDate.isValid()) {
            formControl.setValue(formatDate);
        } else if (formatDate) {
            formControl.setErrors({
                invalidOnChange: true
            });
        }

        this.setFromMaxDate();
    }

    setLocale(locale) {
        this.dateAdapter.setLocale(locale);
        moment.locale(locale);
    }

    hasParseError(formControl): boolean {
        return formControl.hasError('matDatepickerParse') && formControl.getError('matDatepickerParse').text;
    }

    forcePlaceholder(event: any) {
        event.srcElement?.click();
    }

    setFromMaxDate() {
        this.fromMaxDate = (!this.to.value || this.maxDate && (moment(this.maxDate).isBefore(this.to.value))) ? this.maxDate : moment(this.to.value);
    }

    disable() {
        this.editable = false;
        this.from.disable();
        this.to.disable();
    }

    enable() {
        this.editable = true;
        this.from.enable();
        this.to.enable();
    }
}